import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PageHeaderAddButton,
	PageHeaderListActions,
	PopupSnackbar,
	SendEmailModal,
} from "frame/components";
import { makeStyles } from "@material-ui/styles";
import { TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import DocumentPackListItem from "./document-pack-list-item";
import { INFO_HOME_ROUTE } from "features/home/home-routes";
import DocumentPackAddEditModal from "./document-pack-add-edit-modal";
import DocumentPackViewModal from "./document-pack-view-modal";
import {
	createDocumentPack,
	getAllDocumentPacks,
	updateDocumentPack,
	archiveDocumentPack,
	unarchiveDocumentPack,
	deleteDocumentPack,
	emailDocumentPack,
} from "../api";
import { isAccountAdminUser, isSuperAdminUser } from "utils";
import { GENERIC_ERROR_MESSAGE } from "utils";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const mapStateToProps = ({ organisations, profile, staff }) => ({
	currentOrganisationGUID: organisations.currentOrganisation,
	profile,
	staffList: staff.pagination?.results,
});

const mapDispatchToProps = {};

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	filter: {
		display: "flex",
	},
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const DocumentPackList = ({ currentOrganisationGUID, profile, staffList }) => {
	const classes = useStyles();
	const [documentPackList, setDocumentPackList] = useState([]);
	const [selectedDocumentPackIndex, setSelectedDocumentPackIndex] =
		useState(0);
	const [showAddEditModal, setShowAddEditModal] = useState(false);
	const [addEditModalMode, setAddEditModalMode] = useState("Add");
	const [isSavingAddEdit, setIsSavingAddEdit] = useState(false);
	const [showViewModal, setShowViewModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [searchKey, setSearchKey] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const defaultPageSize = 10;
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(0);
	const [hasMoreDocuments, setHasMoreDocuments] = useState(true);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const isInitialMount = useRef(true);
	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	const getDocumentPacks = (
		fromPageToRequest = currentPage,
		recordDeletedOrUpdated = false,
		pageSize = defaultPageSize
	) => {
		// Always fetch if record was deleted/updated, or if we haven't loaded all records yet
		if (recordDeletedOrUpdated || totalNumberOfRecords === 0 || totalNumberOfRecords > documentPackList.length) {
			if (recordDeletedOrUpdated) {
				setHasMoreDocuments(true);
			}

			if (!currentOrganisationGUID) {
				console.warn("No organization GUID available");
				return;
			}

			getAllDocumentPacks(currentOrganisationGUID, {
				ascending: true,
				orderBy: "name",
				page: fromPageToRequest,
				pageSize,
				searchKey: searchKey || ""
			})
				.then((response) => {
					const newResults = response.data.results || [];
					const totalRecords = response.data.totalNumberOfRecords || 0;
					let nextListLength = 0;
					
					// If navigating to first page clear the results from documentPackList otherwise add to the end of it
					if (fromPageToRequest === 1) {
						setDocumentPackList(() => {
							nextListLength = newResults.length;
							return [...newResults];
						});
					} else {
						setDocumentPackList((prev) => {
							const prevList = Array.isArray(prev) ? prev : [];
							const nextList = [...prevList, ...newResults];
							nextListLength = nextList.length;
							return nextList;
						});
					}

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(totalRecords);
					setIsDoneFetching(true);
					
					setHasMoreDocuments(totalRecords > nextListLength);
				})
				.catch((error) => {
					console.error("Error fetching document packs:", error);
					handleApiError(error);
					setDocumentPackList([]);
					setTotalNumberOfRecords(0);
					setIsDoneFetching(true);
					setHasMoreDocuments(false);
				});
		} else {
			setHasMoreDocuments(false);
		}
	};

	const handleApiError = (error) => {
		if (error.response && error.response.status === 401) {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
			getDocumentPacks(1, true);
		} else {
			setErrorMessage(GENERIC_ERROR_MESSAGE);
		}
	};

	// The InfiniteScroll Component requires a height for it to work effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			if (ref.current) {
				setHeight(ref.current.offsetHeight - 20); // reduce height by 20px otherwise the scrollbar flows past the height of screen.
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Initial load - wait for currentOrganisationGUID to be available
	useEffect(() => {
		if (currentOrganisationGUID) {
			getDocumentPacks();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisationGUID]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setDocumentPackList([]);
			setTotalNumberOfRecords(0);
			setHasMoreDocuments(true);
			setIsDoneFetching(false);
			getDocumentPacks(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchKey]);

	return (
		<Page title="Documents" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				<InfiniteScroll
					dataLength={documentPackList.length}
					next={() => {
						getDocumentPacks(currentPage + 1);
					}}
					hasMore={hasMoreDocuments}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />
											<PageHeaderAddButton
							title="Documents"
							addTabTitle="New"
							onAdd={() => {
								setShowAddEditModal(true);
								setAddEditModalMode("Add");
							}}
							staffUserHide={false}
							backActionPath={INFO_HOME_ROUTE}
					titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}
						>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchKey && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchKey("");
																	setCurrentPage(1);
																	setDocumentPackList([]);
																	setTotalNumberOfRecords(0);
																	setHasMoreDocuments(true);
																	setIsDoneFetching(false);
																	getDocumentPacks(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchKey || ""}
												onChange={(val) => {
													const { value } = val.target;
													setSearchKey(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={"e.g. Name, Notes"}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderAddButton>
					<PageContent pad>
						<div className={classes.body}>
							{/* <PaginationHeader
								pagination={{
									...paginationState,
									totalNumberOfPages: totalNumberOfPages,
									totalNumberOfRecords: totalNumberOfRecords,
									pageSize: recordCount,
									pageNumber: paginationState.page,
								}}
							/> */}
							<p style={{ marginTop: 5 }}>
								{documentPackList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
							</p>
							{isDoneFetching && documentPackList.map((documentPack, index) => (
								<DocumentPackListItem
									key={documentPack.documentPackId}
									documentPack={documentPack}
									staffList={staffList}
									showDeleteOption={
										isAccountAdminUser(profile.role) ||
										isSuperAdminUser(profile.role)
									}
									onView={() => {
										setSelectedDocumentPackIndex(index);
										setShowViewModal(true);
									}}
									onEdit={() => {
										setSelectedDocumentPackIndex(index);
										setShowAddEditModal(true);
										setAddEditModalMode("Edit");
									}}
									onDelete={() => {
										const pageSizeToLoad =
											Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;
										deleteDocumentPack(
											currentOrganisationGUID,
											documentPack.documentPackId
										)
											.then(() => {
												getDocumentPacks(1, true, pageSizeToLoad);
												setSuccessMessage(
													"Document deleted successfully."
												);
											})
											.catch(handleApiError);
									}}
									onArchive={() => {
										const pageSizeToLoad =
											Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;
										archiveDocumentPack(
											currentOrganisationGUID,
											documentPack.documentPackId
										)
											.then(() => {
												getDocumentPacks(1, true, pageSizeToLoad);
											})
											.catch(handleApiError);
									}}
									onUnarchive={() => {
										const pageSizeToLoad =
											Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;
										unarchiveDocumentPack(
											currentOrganisationGUID,
											documentPack.documentPackId
										)
											.then(() => {
												getDocumentPacks(1, true, pageSizeToLoad);
											})
											.catch(handleApiError);
									}}
									onEmail={() => {
										setSelectedDocumentPackIndex(index);
										setShowEmailModal(true);
									}}
								/>
							))}
							<PopupSnackbar
								message={errorMessage}
								coloured
								error
								onExited={() => setErrorMessage("")}
							/>
							<PopupSnackbar
								message={successMessage}
								coloured
								success
								onExited={() => setSuccessMessage("")}
							/>
						</div>
						{/* <Pagination
							disabledOnLoadFetch={true}
							onGetList={(newPaginationState) => {
								const mergedPaginationState = {
									...paginationState,
									...newPaginationState,
								};

								setPaginationState(mergedPaginationState);
								getDocumentPackList(mergedPaginationState);
							}}
							pagination={{
								...paginationState,
								totalNumberOfPages: totalNumberOfPages,
								totalNumberOfRecords: totalNumberOfRecords,
								queryPageSize: paginationState.pageSize,
							}}
							orderBy={"name"}
							searchKey={paginationState.searchKey}
						/> */}
					</PageContent>
			<DocumentPackAddEditModal
				isOpen={showAddEditModal}
				documentPack={
					documentPackList[selectedDocumentPackIndex] || {
						attachments: [],
					}
				}
				onCancel={() => {
					if (!isSavingAddEdit) {
						setShowAddEditModal(false);
					}
				}}
				onSave={(documentPack) => {
					setIsSavingAddEdit(true);

					let promise;

					if (addEditModalMode === "Add") {
						promise = createDocumentPack(
							currentOrganisationGUID,
							documentPack
						);
					} else {
						promise = updateDocumentPack(
							currentOrganisationGUID,
							documentPack
						);
					}

					promise
						.then(() => {
							// Refresh the list after save
							const pageSizeToLoad = selectedDocumentPackIndex >= 0
								? Math.ceil((selectedDocumentPackIndex + 1) / defaultPageSize) * defaultPageSize
								: defaultPageSize;
							getDocumentPacks(1, true, pageSizeToLoad);
							setSuccessMessage("Document saved successfully.");
						})
						.catch(handleApiError)
						.finally(() => {
							setIsSavingAddEdit(false);
							setShowAddEditModal(false);
						});
				}}
				isSaving={isSavingAddEdit}
				mode={addEditModalMode}
			/>
			<DocumentPackViewModal
				isOpen={showViewModal}
				documentPack={
					documentPackList[selectedDocumentPackIndex] || {
						attachments: [],
					}
				}
				onClose={() => setShowViewModal(false)}
			/>
			<SendEmailModal
				isOpen={showEmailModal}
				title={
					documentPackList[selectedDocumentPackIndex]
						? "Email Document: " +
						  documentPackList[selectedDocumentPackIndex].name
						: ""
				}
				onClose={() => setShowEmailModal(false)}
				onSend={(selectedRecipients) => {
					setIsSendingEmail(true);
					emailDocumentPack(
						currentOrganisationGUID,
						documentPackList[selectedDocumentPackIndex]
							.documentPackId,
						selectedRecipients.recipients,
						[
							...selectedRecipients.recipientsOther,
							...selectedRecipients.recipientsCustomerSites,
						]
					)
						.then(() => {
							setSuccessMessage("Emails sent successfully.");
						})
						.catch(handleApiError)
						.finally(() => {
							setIsSendingEmail(false);
							setShowEmailModal(false);
						});
				}}
				isSaving={isSendingEmail}
				emailListType={"allCustomerSiteRecord"}
			/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreDocuments && <CardText message={"All documents have been loaded."} />}
					{documentPackList.length === 0 && isDoneFetching && (
						<CardText message={"No documents to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentPackList);
